import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useObjectUrl } from "@redotech/react-util/url";
import { AiFileAnalysis } from "@redotech/redo-common-model/file/uploaded-file";
import { useTranslation } from "@redotech/redo-i18n";
import FileIcon from "@redotech/redo-icons/file-02.svg";
import PlusCircleIcon from "@redotech/redo-icons/plus-circle.svg";
import { Input } from "@redotech/ui/form";
import {
  ChangeEventHandler,
  createContext,
  DragEventHandler,
  memo,
  useId,
  useState,
} from "react";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import CircleSpinner from "./circle-spinner.svg";
import { Flex } from "./flex";
import XIcon from "./icon-old/x.svg";
import {
  extractFileNameFromUrl,
  extractUploadErrorMessage,
} from "./image-upload-utils";
import * as imageUploadCss from "./image-upload.module.css";
import { LabeledInput } from "./labeled-input";
import { Text } from "./text";

export interface ImageUploadProps {
  id?: string;
  value: URL | undefined;
  preUpload?: () => Promise<void> | void;
  onChange?(value: URL | undefined): void;
  onError?: (error: unknown) => void;
  /** By default, clicking on the upload button opens the system's
   * file picker. Use this field to override that behavior. */
  onClickOverride?: () => void;
  disabled?: boolean;
  className?: string;
  caption?: string;
  maxFileSizeKb?: number | undefined;
  /**
   * Hard constraint on image width in pixels.
   * Can be used together with recommendedLongestSidePx - both constraints will be applied.
   */
  maxImageWidthPx?: number | undefined;
  /**
   * Recommended maximum dimension for the longest side (width or height) in pixels.
   * If the uploaded image exceeds this size, it will be resized so that the longest side matches this value.
   * Prioritizes file size optimization over maintaining image resolution.
   * Best for use in scenarios with hard file size constraints (e.g., MMS images).
   * Can be used together with maxImageWidthPx - both constraints will be applied.
   */
  recommendedLongestSidePx?: number | undefined;
  autoOptimizeImage?: boolean | undefined;
  showErrorMessages?: boolean | undefined;
  accept?: readonly string[] | undefined;
  preserveGifs?: boolean | undefined;
  /**
   * Controls the upload interaction and preview style.
   * - `"image"` (default): shows a background-image preview after upload.
   * - `"file"`: shows a document icon with the filename after upload; suits
   *   PDF/document uploads where an image preview is not meaningful.
   */
  variant?: "image" | "file";
}

export const ImageUpload = memo(function ImageUpload({
  id,
  onChange,
  value,
  preUpload,
  onError,
  className,
  disabled = false,
  caption,
  maxFileSizeKb,
  maxImageWidthPx,
  recommendedLongestSidePx,
  autoOptimizeImage,
  showErrorMessages = false,
  accept,
  onClickOverride,
  preserveGifs,
  variant = "image",
}: ImageUploadProps) {
  return (
    <MediaUpload
      accept={accept}
      autoOptimizeImage={autoOptimizeImage}
      caption={caption}
      className={className}
      disabled={disabled}
      id={id}
      maxFileSizeKb={maxFileSizeKb}
      maxImageWidthPx={maxImageWidthPx}
      mediaType="image"
      onChange={onChange}
      onClickOverride={onClickOverride}
      onError={onError}
      preserveGifs={preserveGifs}
      preUpload={preUpload}
      recommendedLongestSidePx={recommendedLongestSidePx}
      showErrorMessages={showErrorMessages}
      value={value}
      variant={variant}
    />
  );
});

export const VideoUpload = memo(function VideoUpload({
  id,
  onChange,
  value,
  className,
  disabled = false,
  caption,
  onClickOverride,
}: ImageUploadProps) {
  return (
    <MediaUpload
      caption={caption}
      className={className}
      disabled={disabled}
      id={id}
      mediaType="video"
      onChange={onChange}
      onClickOverride={onClickOverride}
      value={value}
    />
  );
});

const MediaUpload = memo(function MediaUpload({
  id,
  onChange,
  onError,
  preUpload,
  value,
  className,
  disabled = false,
  caption,
  mediaType,
  maxFileSizeKb,
  maxImageWidthPx,
  recommendedLongestSidePx,
  autoOptimizeImage,
  showErrorMessages = false,
  accept,
  onClickOverride,
  preserveGifs,
  variant = "image",
}: ImageUploadProps & { mediaType: "image" | "video" }) {
  const { t } = useTranslation();
  const uploader = useRequiredContext(UploaderContext);

  const [inputElement, setInputElement] = useState<HTMLInputElement | null>();

  const [file, setFile] = useState<File | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const setUploadErrorMessage = useHandler((error: unknown) => {
    setErrorMessage(extractUploadErrorMessage(error));
  });

  const [uploadLoad, doUpload] = useTriggerLoad(async (signal) => {
    try {
      setErrorMessage(undefined);
      await preUpload?.();
      const newValue = file
        ? (
            await uploader({
              file,
              maxFileSizeKb,
              maxImageWidthPx,
              recommendedLongestSidePx,
              autoOptimizeImage,
              preserveGifs,
              signal,
            })
          ).url
        : undefined;
      setFile(undefined);
      onChange?.(newValue);
    } catch (error) {
      setFile(undefined);
      setUploadErrorMessage(error);
      onError?.(error);
    }
  });

  const objectUrl = useObjectUrl(file);

  const fileName =
    variant === "file"
      ? (file?.name ?? extractFileNameFromUrl(value))
      : undefined;

  const handleRemove = useHandler(() => {
    setFile(undefined);
    setErrorMessage(undefined);
    // Reset the input element so the same file can be selected again
    if (inputElement) {
      inputElement.value = "";
    }
    doUpload();
  });
  const handleUpload = useHandler(() => inputElement?.click());

  const url = objectUrl || value?.toString();

  const handleClick = useHandler(() => {
    if (url) {
      window.open(url, "_blank");
    } else {
      handleUpload();
    }
  });

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setFile(event.target.files![0]);
    doUpload();
  };

  const dropHandler: DragEventHandler = (event) => {
    event.preventDefault();
    if (event.dataTransfer.items) {
      for (const item of event.dataTransfer.items) {
        const file = item?.getAsFile();
        if (file) {
          setFile(file);
          doUpload();
        }
      }
    } else if (event.dataTransfer.files) {
      for (const file of event.dataTransfer.files) {
        setFile(file);
        doUpload();
      }
    }
  };

  const cancelDrag: DragEventHandler = (event) => {
    event.preventDefault();
  };

  return (
    <Flex
      className={className}
      dir="column"
      grow={1}
      onDragOver={cancelDrag}
      onDrop={dropHandler}
      position="relative"
    >
      <input
        accept={accept?.join(",")}
        className={imageUploadCss.input}
        id={id || `image-upload-${mediaType}`}
        onChange={handleChange}
        ref={setInputElement}
        type="file"
      />

      <Flex
        bgColor="primary"
        borderColor="secondary"
        borderRadius="md"
        borderStyle="solid"
        borderWidth="1px"
        className={[
          imageUploadCss.imageButtonContainer,
          variant === "file" && url && imageUploadCss.filePreview,
          disabled && imageUploadCss.disabled,
        ]
          .filter(Boolean)
          .join(" ")}
        cursor="pointer"
        grow={1}
        overflow="hidden"
        position="relative"
      >
        {mediaType === "video" && url && (
          <Flex
            className={imageUploadCss.videoContainer}
            height="full"
            justify="center"
            position="absolute"
            w="full"
          >
            <video>
              <source src={url} />
              <a href={url}>{t("Download video")}</a>
            </video>
          </Flex>
        )}
        <button
          className={imageUploadCss.imageButton}
          disabled={disabled}
          onClick={onClickOverride ?? handleClick}
          {...(variant === "image" &&
            mediaType === "image" &&
            url && { style: { backgroundImage: `url(${url})` } })}
          type="button"
        >
          {variant === "file" && url ? (
            <Flex align="center" gap="sm" px="md" py="sm">
              <FileIcon />
              <Text
                className={imageUploadCss.fileName}
                fontSize="sm"
                textColor="primary"
              >
                {fileName}
              </Text>
            </Flex>
          ) : !url ? (
            <Flex
              align="center"
              dir="column"
              gap="xs"
              justify="center"
              position="relative"
              px="3xl"
              py="xl"
            >
              <PlusCircleIcon />
              <Text fontSize="sm" mt="md" textColor="primary">
                {variant === "file"
                  ? t("Add file")
                  : mediaType === "image"
                    ? t("Add image")
                    : t("Add video")}
              </Text>
            </Flex>
          ) : null}
        </button>
        {url && (
          <RedoButton
            aria-label={t("Remove")}
            className={
              variant === "file"
                ? imageUploadCss.removeButtonFile
                : imageUploadCss.removeButton
            }
            disabled={disabled}
            hierarchy={variant === "file" ? "tertiary" : "secondary"}
            IconLeading={XIcon}
            onClick={handleRemove}
            size="xs"
            type="button"
          />
        )}
        {uploadLoad.pending && (
          <div className={imageUploadCss.spinnerContainer}>
            <CircleSpinner className={imageUploadCss.spinner} />
          </div>
        )}
      </Flex>
      {caption && (
        <Text fontSize="xs" textColor="secondary">
          {caption}
        </Text>
      )}
      {showErrorMessages && errorMessage && (
        <Text fontSize="xs" textColor="error">
          {errorMessage}
        </Text>
      )}
    </Flex>
  );
});

export const FormImageUpload = memo(function FormImageUpload({
  label,
  description,
  input,
  disabled = false,
  manualErrors,
  className,
  maxFileSizeKb,
  maxImageWidthPx,
  autoOptimizeImage,
  ...props
}: {
  description?: string;
  input: Input<URL | undefined>;
  label: string;
  disabled?: boolean;
  manualErrors?: string[];
  className?: string;
} & Omit<ImageUploadProps, "value" | "onChange">) {
  const id = useId();
  return (
    <LabeledInput
      description={description}
      errors={
        manualErrors?.length ? manualErrors : input.changed ? input.errors : []
      }
      id={id}
      label={label}
    >
      <ImageUpload
        autoOptimizeImage={autoOptimizeImage}
        className={className}
        disabled={disabled}
        id={id}
        maxFileSizeKb={maxFileSizeKb}
        maxImageWidthPx={maxImageWidthPx}
        onChange={input.setValue}
        value={input.value}
        {...props}
      />
    </LabeledInput>
  );
});

/**
 * Upload image
 */
export type Uploader = ({
  file,
  maxFileSizeKb,
  maxImageWidthPx,
  recommendedLongestSidePx,
  autoOptimizeImage,
  preserveGifs,
  signal,
}: {
  file: File;
  maxFileSizeKb?: number | undefined;
  maxImageWidthPx?: number | undefined;
  recommendedLongestSidePx?: number | undefined;
  autoOptimizeImage?: boolean | undefined;
  preserveGifs?: boolean | undefined;
  signal: AbortSignal;
}) => Promise<{ url: URL; analysis?: AiFileAnalysis }>;

export const UploaderContext = createContext<Uploader | undefined>(undefined);

export const UploaderProvider = UploaderContext.Provider;
